<script setup lang="ts">
import NewsDetailHeader from './NewsDetailHeader.vue'
import NewsDetailContent from './NewsDetailContent.vue'
import NewsDetailRecommended from './NewsDetailRecommended.vue'
import { useAPIRequest } from '~/composables/api/useAPIRequest'
import type { Article } from '~/types/api/Article'

const { t } = useI18n()
const route = useRoute()
const articleId = route.params.id

const articleDetailResponse = await useAsyncData<Article>(async () => {
  return await useAPIRequest(`/articles/detail/${articleId}`)
})

if (articleDetailResponse.error.value) {
  throw new Error(articleDetailResponse.error.value.message)
}

const { data: article } = articleDetailResponse
if (!article.value) {
  throw createError({
    statusCode: 404,
    statusMessage: t('pages.news.detail.notFound'),
  })
}

// redirect, if url slug is not matching the slug in fetched data
if (article.value && article.value?.slug !== route.params.slug) {
  await navigateTo({
    path: useLocaleLink({
      name: 'article-slug-id',
      params: {
        slug: article.value.slug,
        id: article.value.id,
      },
    })?.fullPath,
  })
}

useHead({
  title: article.value.name,
  meta: [
    {
      name: 'description',
      content: articleDetailResponse.data.value?.short_description,
    },
  ],
})

defineOgImage({
  component: 'root',
  url: articleDetailResponse.data.value?.image,
  width: 1200,
  height: 630,
})
</script>

<template>
  <div v-if="article" class="c-news-detail">
    <NewsDetailHeader class="c-news-detail__header" :article="article" />

    <NewsDetailContent class="c-news-detail__content" :article="article" />

    <NewsDetailRecommended
      class="c-news-detail__recommended"
      :article="article"
    />
  </div>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;

.c-news-detail {
  &__header {
    margin-bottom: 64px;
  }
  &__content {
    margin-bottom: 120px;

    @include breakpoint-down('sm') {
      margin-bottom: 60px;
    }
  }
  &__recommended {
    margin-bottom: 120px;

    @include breakpoint-down('sm') {
      margin-bottom: 60px;
    }
  }
}
</style>
